import { StrictMode, type ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { SigningPage } from './SigningPage.tsx';
import { SetupPage } from './SetupPage.tsx';
import { LoginPage } from './LoginPage.tsx';
import { AdminPage } from './AdminPage.tsx';
import { EnvelopesPage } from './EnvelopesPage.tsx';
import { EnvelopePage } from './EnvelopePage.tsx';
import { Version } from './Version.tsx';
import './styles.css';

// The token arrives as the last path segment of the signing link
// (spec/04 §5). It goes into a request body from here, never into a query
// string we then log.
const path = window.location.pathname.replace(/\/+$/, '');
const token = path.split('/').pop() ?? '';

// /setup is its own path rather than something the holding page redirects to.
// An instance is claimed exactly once, and a root that silently became a
// setup form the moment the database looked empty is the shape that claims an
// instance by accident (spec/04 §8c).
function Routed(): ReactElement {
  if (path === '/setup') return <SetupPage />;
  if (path === '/login') return <LoginPage />;
  if (path === '/admin') return <AdminPage />;
  if (path === '/envelopes') return <EnvelopesPage />;
  // /envelopes/{id}. Matched rather than parsed by a router: this app has five
  // routes, and a router is a dependency for a switch statement.
  const envelope = /^\/envelopes\/([A-Za-z0-9_-]+)$/.exec(path);
  if (envelope !== null) return <EnvelopePage id={envelope[1] ?? ''} />;
  if (token === '' || path === '') {
    return (
      <main className="wrap">
        <h1>signrs</h1>
        <p className="muted">
          Open the link you were sent to read and sign a document.
        </p>
      </main>
    );
  }
  return <SigningPage token={token} />;
}

// The footer is here rather than in each page, so a page added later cannot
// forget it — "all pages" is a property of the shell, not a convention six
// components are expected to keep (spec/04 §1).
function App(): ReactElement {
  return (
    <>
      <Routed />
      <Version />
    </>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <StrictMode>
    <App />
  </StrictMode>,
);
